import valueEqual from 'value-equal'

import {
  COMPANY_SEARCH,
  COMPANY_SEARCH_WITH_DETAIL,
  JOB_SEARCH,
  JOB_SEARCH_WITH_DETAIL
} from '@/page/constants'
import { enforcePageType } from '@/page/store/actions'
import { getPageType } from '@/page/store/selectors'

import { FILTERS, SEARCH } from '../config'
import { COMPANY, DEFAULT_DISTANCE, JOB } from '../search/constants'
import {
  job,
  company,
  setSearchFilters,
  setSearchQuery,
  setSearchType,
  showFirstResult
} from '../search/store/actions'
import {
  defaultOrder,
  defaultSelectedId,
  defaultShowFirstResult
} from '../search/store/defaultState'
import {
  getSearchFilters,
  getSearchOrder,
  getSearchQuery,
  getSearchType,
  getSelectedCompanyId,
  getSelectedJobId,
  getShowFirstResult,
  getUpdateUrl
} from '../search/store/selectors'

const SELECTED_ID_PARAM = {
  [JOB]: 'job-id',
  [COMPANY]: 'company-id'
}

const valueReducer = (key, shouldOutput = v => v, convert = v => `${v}`) => (location, newValue, oldValue) => {
  const { query: { [key]: _, ...query }, ...rest } = location
  return {
    ...rest,
    query: {
      ...query,
      ...(shouldOutput(newValue) ? { [key]: [convert(newValue)] } : null)
    }
  }
}

const filterReducer = filterName => (location, newValue, oldValue) => {
  const { query: { [filterName]: _, ...query }, ...rest } = location
  return {
    ...rest,
    query: {
      ...query,
      ...(newValue && newValue.length > 0
        ? { [filterName]: newValue.map(v => `${v}`) }
        : null)
    }
  }
}

const filterAction = filtername => ({ query }, currentFilter) => {
  const newFilter = filtername in query ? query[filtername].map(v => parseInt(v)) : []
  if (valueEqual(currentFilter, newFilter)) {
    return
  }
  return setSearchFilters({ [filtername]: newFilter })
}

const getValueFromLocation = ({ query }, queryKey, converter = (v) => v) => {
  return queryKey in query && query[queryKey].length > 0 && query[queryKey][0] != null ? converter(query[queryKey][0]) : null
}

const valueAction = (queryKey, action, converter) => (location, currentValue) => {
  const id = getValueFromLocation(location, queryKey, converter)
  if (currentValue === id) {
    return
  }
  return action(id)
}

const getSearchTypeFromLocation = ({ pathname }) => {
  const [type] = Object.entries(SEARCH.types).find(
    ([type, { location }]) => pathname === location
  ) || []
  return type
}

const getPageTypeFromLocation = (location) => {
  const type = getSearchTypeFromLocation(location)
  if (!(type in SELECTED_ID_PARAM)) {
    return
  }
  const selectedId = getValueFromLocation(location, SELECTED_ID_PARAM[type])
  switch (type) {
    case JOB: return selectedId ? JOB_SEARCH_WITH_DETAIL : JOB_SEARCH
    case COMPANY: return selectedId ? COMPANY_SEARCH_WITH_DETAIL : COMPANY_SEARCH
  }
}

export default [
  { // pathname
    selector: state => ({
      type: getSearchType(state),
      updateUrl: getUpdateUrl(state)
    }),
    reducer: (location, { type, updateUrl }) =>
      (!updateUrl || !(type in SEARCH.types)
        ? location
        : {
            ...location,
            pathname: SEARCH.types[type].location
          }),
    action: (location, { type: currentType }) => {
      const type = getSearchTypeFromLocation(location)
      if (type && type !== currentType) {
        return setSearchType(type)
      }
    }
  },
  { // name=
    selector: state => getSearchQuery(state).name,
    reducer: valueReducer('name'),
    action: ({ query: { name: [name = ''] = [] } }, currentName) => currentName !== name ? setSearchQuery({ name }) : null
  },
  { // plz=
    selector: state => {
      const { location = '', dist = DEFAULT_DISTANCE, cc = '' } = getSearchQuery(state)
      return location && [location, ...(dist && [dist, ...(cc && [cc])])].join(':')
    },
    reducer: valueReducer('plz'),
    action: ({ query: { plz: [plz = ''] = [] } }, currentPlz) => {
      if (plz === currentPlz) {
        return
      }
      const [location = '', dist = DEFAULT_DISTANCE, cc = ''] = plz.split(':')
      return setSearchQuery({
        location,
        dist: parseInt(dist) || DEFAULT_DISTANCE,
        cc
      })
    }
  },
  ...FILTERS.map(({ id }) => ({
    selector: state => getSearchFilters(state)[id],
    reducer: filterReducer(id),
    action: filterAction(id)
  })),
  { // order=
    selector: state => getSearchOrder(state) || defaultOrder,
    reducer: valueReducer('order', (order) => order !== defaultOrder, (order) => order !== defaultOrder ? order : null),
    action: ({ query: { order: [order = defaultOrder] = [] } }, currentOrder) =>
      currentOrder !== order ? setSearchQuery({ order }) : null
  },
  { // job-id=
    selector: state => getSelectedJobId(state) || defaultSelectedId,
    reducer: valueReducer(SELECTED_ID_PARAM[JOB], v => v !== defaultSelectedId),
    action: valueAction(SELECTED_ID_PARAM[JOB], job.setSelectedId)
  },
  { // company-id=
    selector: state => getSelectedCompanyId(state) || defaultSelectedId,
    reducer: valueReducer(SELECTED_ID_PARAM[COMPANY], v => v !== defaultSelectedId),
    action: valueAction(SELECTED_ID_PARAM[COMPANY], company.setSelectedId, parseInt)
  },
  { // restore getShowFirstResult
    selector: state => getShowFirstResult(state) || defaultShowFirstResult,
    action: (location) => {
      const type = getSearchTypeFromLocation(location)
      if (!(type in SELECTED_ID_PARAM)) {
        if (process.env.NODE_ENV !== 'production') {
          throw new Error(`Unhandled search type ${type}`)
        } else {
          return
        }
      }
      const selectedId = getValueFromLocation(location, SELECTED_ID_PARAM[type])
      return showFirstResult(!!selectedId)
    }
  },
  { // page.type
    selector: (state) => ({ pageType: getPageType(state) }),
    action: (location, { pageType: currentPageType }) => {
      const pageType = getPageTypeFromLocation(location)
      if (!pageType) {
        if (process.env.NODE_ENV !== 'production') {
          throw new Error(`Undetermined page type for location ${JSON.stringify(location)}`)
        } else {
          return
        }
      }
      if (pageType !== currentPageType) {
        return enforcePageType(pageType)
      }
    }
  },
  { // remove apply= - this is handled on backend and needs to be cleared on
    // location change
    reducer: ({ query: { apply, ...restOfQuery }, ...restOfLocation }) => ({
      ...restOfLocation,
      query: restOfQuery
    })
  },
  { // offset=
    reducer: valueReducer('offset', () => false),
    shouldTriggerSearch: ({ query: { offset } }, { query: { offset: prevOffset } }) => {
      return !offset && prevOffset
    }
  }
]
